import { useState } from "react"
import { Link, useLocation } from "react-router-dom"
import { Menu, X, Sun, Moon } from "lucide-react"
import { useTheme } from "@/components/theme-provider"
import { cn } from "@/lib/utils"
import { dimensionsFromHeight, MASCOT_INTRINSIC } from "@/lib/brand-assets"

const NAV_LINKS = [
  { href: "/#home", label: "Strona Glowna" },
  { href: "/#o-nas", label: "O nas" },
  { href: "/ogloszenia", label: "Ogloszenia" },
  { href: "/asortyment", label: "Asortyment" },
  { href: "/galeria", label: "Galeria" },
  { href: "/smaki", label: "Smaki" },
  { href: "/#kontakt", label: "Kontakt" },
]

export function Navbar() {
  const [open, setOpen] = useState(false)
  const { theme, setTheme } = useTheme()
  const location = useLocation()

  const resolvedTheme =
    theme === "system"
      ? window.matchMedia("(prefers-color-scheme: dark)").matches
        ? "dark"
        : "light"
      : theme

  const handleNavClick = (href: string) => {
    setOpen(false)
    if (href.startsWith("/#") && location.pathname === "/") {
      const id = href.slice(2)
      if (id === "home") {
        window.scrollTo({ top: 0, behavior: "smooth" })
        return
      }

      const element = document.getElementById(id)
      if (element) {
        element.scrollIntoView({ behavior: "smooth" })
      }
    }
  }

  return (
    <header className="fixed top-0 right-0 left-0 z-50 border-b border-border/50 bg-background/80 backdrop-blur-xl">
      <nav className="mx-auto flex max-w-7xl items-center justify-between px-6 py-4">
        {/* Logo */}
        <Link to="/" className="flex items-center gap-3 text-zielarz-green">
          <img
            src="/assets/brand/mascot.svg"
            alt="Zielarz Logo"
            className="h-11 w-auto object-contain sm:h-14"
            {...dimensionsFromHeight(MASCOT_INTRINSIC, 44)}
            onError={(e) => {
              e.currentTarget.style.display = "none"
            }}
          />
        </Link>

        {/* Desktop links */}
        <div className="hidden items-center gap-8 md:flex">
          {NAV_LINKS.map((link) => {
            const isHashLink = link.href.includes("#")
            const isActive = !isHashLink && location.pathname === link.href

            if (!isHashLink) {
              return (
                <Link
                  key={link.href}
                  to={link.href}
                  onClick={() => setOpen(false)}
                  className={cn(
                    "relative text-sm font-medium transition-colors after:absolute after:bottom-[-4px] after:left-0 after:h-[2px] after:w-0 after:bg-zielarz-green after:transition-all hover:text-zielarz-green hover:after:w-full",
                    isActive
                      ? "text-zielarz-green after:w-full"
                      : "text-foreground/70"
                  )}
                >
                  {link.label}
                </Link>
              )
            }

            return (
              <a
                key={link.href}
                href={link.href}
                onClick={() => handleNavClick(link.href)}
                className="relative text-sm font-medium text-foreground/70 transition-colors after:absolute after:bottom-[-4px] after:left-0 after:h-[2px] after:w-0 after:bg-zielarz-green after:transition-all hover:text-zielarz-green hover:after:w-full"
              >
                {link.label}
              </a>
            )
          })}

          {/* Theme toggle */}
          <button
            onClick={() =>
              setTheme(resolvedTheme === "dark" ? "light" : "dark")
            }
            className="rounded-full border border-border p-2 transition-all hover:border-zielarz-green hover:bg-zielarz-green/10"
            aria-label="Zmien motyw"
          >
            {resolvedTheme === "dark" ? (
              <Sun className="size-4 text-zielarz-yellow" />
            ) : (
              <Moon className="size-4 text-zielarz-green" />
            )}
          </button>
        </div>

        {/* Mobile toggle */}
        <div className="flex items-center gap-3 md:hidden">
          <button
            onClick={() =>
              setTheme(resolvedTheme === "dark" ? "light" : "dark")
            }
            className="rounded-full border border-border p-2"
            aria-label="Zmien motyw"
          >
            {resolvedTheme === "dark" ? (
              <Sun className="size-4 text-zielarz-yellow" />
            ) : (
              <Moon className="size-4 text-zielarz-green" />
            )}
          </button>
          <button
            onClick={() => setOpen(!open)}
            className="rounded-lg border border-border p-2"
            aria-label="Menu"
          >
            {open ? <X className="size-5" /> : <Menu className="size-5" />}
          </button>
        </div>
      </nav>

      {/* Mobile menu */}
      <div
        className={cn(
          "overflow-hidden border-t border-border/50 bg-background/95 backdrop-blur-xl transition-all duration-300 md:hidden",
          open ? "max-h-96" : "max-h-0 border-t-0"
        )}
      >
        <div className="flex flex-col gap-1 px-6 py-4">
          {NAV_LINKS.map((link) => {
            const isHashLink = link.href.includes("#")
            const isActive = !isHashLink && location.pathname === link.href

            if (!isHashLink) {
              return (
                <Link
                  key={link.href}
                  to={link.href}
                  onClick={() => setOpen(false)}
                  className={cn(
                    "rounded-lg px-4 py-3 text-sm font-medium transition-colors hover:bg-zielarz-green/10 hover:text-zielarz-green",
                    isActive ? "bg-zielarz-green/10 text-zielarz-green" : ""
                  )}
                >
                  {link.label}
                </Link>
              )
            }

            return (
              <a
                key={link.href}
                href={link.href}
                onClick={() => handleNavClick(link.href)}
                className="rounded-lg px-4 py-3 text-sm font-medium transition-colors hover:bg-zielarz-green/10 hover:text-zielarz-green"
              >
                {link.label}
              </a>
            )
          })}
        </div>
      </div>
    </header>
  )
}
